import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MaterialModule} from '../../../material.module';
import {ClientService} from '../../../services/auth/client.service';
import {ClientRetentionService} from '../../../services/objects/client-retention.service';
import {ToastService} from '../../../services/layout/toast.service';
import {IClientRetentionSettings} from '../../../models/client-retention-settings.model';
import {normalizeConversationCategories} from '../../../models/chatbot-options.model';

@Component({
  selector: 'app-conversation-settings',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule],
  templateUrl: './conversation-settings.component.html',
  styleUrls: ['./conversation-settings.component.scss']
})
export class ConversationSettingsComponent implements OnInit, OnChanges {
  private readonly clientService = inject(ClientService);
  private readonly clientRetentionService = inject(ClientRetentionService);
  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);

  @Input() settings: IClientRetentionSettings | null = null;
  @Output() settingsSaved = new EventEmitter<IClientRetentionSettings>();

  isSavingRetention = false;
  usesLegacyChatbotCategories = false;

  settingsForm: FormGroup = this.fb.group({
    conversationRetentionDays: [null, [Validators.min(1), Validators.max(3650)]],
    conversationCategoriesText: ['']
  });

  ngOnInit(): void {
    this.applySettings(this.settings);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      this.applySettings(this.settings);
    }
  }

  saveConversationSettings(): void {
    if (!this.clientService.client?.id) {
      this.toastService.showError('No client selected.', 3000);
      return;
    }

    const control = this.settingsForm.get('conversationRetentionDays');
    control?.markAsTouched();

    if (control?.invalid) {
      this.toastService.showError('Conversation retention days must be between 1 and 3650.', 3000);
      return;
    }

    const raw = control?.value;
    const conversationRetentionDays = raw === null || raw === '' ? null : Number(raw);
    const conversationCategories = normalizeConversationCategories(
      this.settingsForm.get('conversationCategoriesText')?.value
    );

    this.isSavingRetention = true;
    this.clientRetentionService.saveAsync(conversationRetentionDays, conversationCategories).subscribe({
      next: (resp) => {
        this.applySettings(resp);
        this.settingsSaved.emit(resp);
        this.toastService.showSuccess('Conversation settings saved.', 3000);
        this.isSavingRetention = false;
      },
      error: (error) => {
        console.error('Error saving conversation settings', error);
        this.toastService.showError('Failed to save conversation settings.', 3000);
        this.isSavingRetention = false;
      }
    });
  }

  private applySettings(settings: IClientRetentionSettings | null): void {
    if (!settings) {
      return;
    }

    this.settingsForm.patchValue({
      conversationRetentionDays: settings.conversationRetentionDays,
      conversationCategoriesText: (settings.conversationCategories ?? []).join('\n')
    }, {emitEvent: false});
    this.usesLegacyChatbotCategories = settings.usesLegacyChatbotCategories === true;
  }
}
